import React, { useState, useEffect, useMemo } from 'react';
import {
    TrendingUp,
    TrendingDown,
    Calendar,
    Loader2,
    Building2,
    CreditCard,
    PlusCircle,
    Trash2,
    X,
    Shield,
    Receipt,
    Clock,
    AlertTriangle,
} from 'lucide-react';
import { useAuth } from "../contexts/AuthContext";
import { SedeService, AmministrazioneService } from '../services/apiService';
import { FinancialStats, MovimentoContabile, PraticaNonSaldata, PraticaNonFatturata, ServizioNonSaldato, ServizioNonFatturato, UserRole } from '../types';
import { useToast } from "../components/ToastContext";
import { Modal } from "../components/Modal";
import {useNavigate} from "react-router-dom";
import NumberInput from '../components/NumberInput';
import MovimentiUnaTantumCard, { limitiMese } from '../components/MovimentiUnaTantumCard';
import { localIsoDate } from '../utils/utils';

export const AmministrazioneView: React.FC = () => {
    const { currentSedeId, user } = useAuth();
    const { addToast } = useToast();
    const navigate = useNavigate();
    const isAdmin = user?.role === UserRole.ADMIN;

    // Dati
    const [currentSedeName, setCurrentSedeName] = useState<string>("Tutte le Sedi");
    const [loading, setLoading] = useState<boolean>(true);

    // Dati finanziari
    const [stats, setStats] = useState<FinancialStats>({ fatturato: 0, incassato: 0, spese: 0, utile: 0, trend: 0 });
    const [movimenti, setMovimenti] = useState<MovimentoContabile[]>([]);
    const [praticheNonSaldate, setPraticheNonSaldate] = useState<PraticaNonSaldata[]>([]);
    const [praticheNonFatturate, setPraticheNonFatturate] = useState<PraticaNonFatturata[]>([]);
    // Equivalenti per i servizi fiduciari.
    const [serviziNonSaldati, setServiziNonSaldati] = useState<ServizioNonSaldato[]>([]);
    const [serviziNonFatturati, setServiziNonFatturati] = useState<ServizioNonFatturato[]>([]);

    // Movimenti separati per tipologia (PRATICA / SERVIZIO).
    const movimentiPratiche = useMemo(
        () => movimenti.filter(m => (m.entityType || 'PRATICA') === 'PRATICA'),
        [movimenti]
    );
    const movimentiServizi = useMemo(
        () => movimenti.filter(m => m.entityType === 'SERVIZIO'),
        [movimenti]
    );

    // Spese fisse
    const [speseFisse, setSpeseFisse] = useState<any[]>([]);
    const [showAddSpesaModal, setShowAddSpesaModal] = useState(false);
    const [nuovaSpesa, setNuovaSpesa] = useState({ voce: '', importo: 0 });
    // Con "Tutte le sedi" selezionato non si possono aggiungere spese fisse:
    // sono legate a una sede e verrebbero salvate con sedeId=null, comparendo ovunque.
    const [showSelectSedeModal, setShowSelectSedeModal] = useState(false);

    // Modale storico variazioni spese fisse.
    const [showStoricoSpeseModal, setShowStoricoSpeseModal] = useState(false);

    // Mandati usciti dai KPI (archiviati o eliminati) con residuo non riscosso.
    // Caricati senza filtro date: il banner mostra il totale complessivo, che non
    // deve poter sparire. Il periodo si restringe nella modale.
    const [nonRiscossi, setNonRiscossi] = useState<PraticaNonSaldata[]>([]);
    // Mese della pagina (card una tantum e riquadro dei mandati usciti dai KPI).
    const [mese, setMese] = useState<string>(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [showNonRiscossiModal, setShowNonRiscossiModal] = useState(false);

    const totaleNonRiscossi = useMemo(
        () => nonRiscossi.reduce((acc, p) => acc + p.importoResiduo, 0),
        [nonRiscossi]
    );
    // Non fatturato e non riscosso sono debiti diversi: non vanno sommati.
    const totaleNonFatturato = useMemo(
        () => nonRiscossi.reduce((acc, p) => acc + (p.importoDaFatturare || 0), 0),
        [nonRiscossi]
    );
    const nEliminati = useMemo(
        () => nonRiscossi.filter(p => p.motivoUscita === 'ELIMINATO').length,
        [nonRiscossi]
    );

    const totaleSpeseFisse = useMemo(() =>
            speseFisse.reduce((acc, curr) => acc + curr.importo, 0),
        [speseFisse]);

    // Caricamento dati
    const loadDashboardData = async () => {
        setLoading(true);
        try {
            if (currentSedeId === 'ALL' || currentSedeId === null) {
                setCurrentSedeName("Tutte le Sedi");
            } else if (typeof currentSedeId === 'number') {
                const sedeRes = await SedeService.getById(currentSedeId);
                if (sedeRes.success && sedeRes.data) setCurrentSedeName(sedeRes.data.name);
            }

            const sedeIdForService = currentSedeId ?? 'ALL';

            // Carica stats, movimenti, spese fisse e pratiche/servizi non saldati/fatturati.
            const [statsRes, movimentiRes, speseFisseRes, nonSaldateRes, nonFaturateRes, servNonSaldRes, servNonFattRes, archiviatiRes] = await Promise.all([
                AmministrazioneService.getStats(sedeIdForService),
                AmministrazioneService.getMovimenti(sedeIdForService),
                AmministrazioneService.getSpeseFisse(sedeIdForService),
                AmministrazioneService.getPraticheNonSaldate(sedeIdForService),
                AmministrazioneService.getPraticheNonFatturate(sedeIdForService),
                AmministrazioneService.getServiziNonSaldati(sedeIdForService),
                AmministrazioneService.getServiziNonFatturati(sedeIdForService),
                // Solo i mandati e servizi usciti dai KPI nel mese della pagina.
                AmministrazioneService.getImportiNonRiscossi({ sedeId: sedeIdForService, dal: limitiMese(mese).from, al: limitiMese(mese).to }),
            ]);

            if (statsRes.success) setStats(statsRes.data);
            if (movimentiRes.success) setMovimenti(movimentiRes.data);
            if (speseFisseRes.success && speseFisseRes.data) setSpeseFisse(speseFisseRes.data);
            if (nonSaldateRes.success && nonSaldateRes.data) setPraticheNonSaldate(nonSaldateRes.data);
            if (nonFaturateRes.success && nonFaturateRes.data) setPraticheNonFatturate(nonFaturateRes.data);
            if (servNonSaldRes.success && servNonSaldRes.data) setServiziNonSaldati(servNonSaldRes.data);
            if (servNonFattRes.success && servNonFattRes.data) setServiziNonFatturati(servNonFattRes.data);
            if (archiviatiRes.success) setNonRiscossi(archiviatiRes.data || []);

        } catch (error) {
            console.error("Errore caricamento dati amministrazione", error);
            addToast("Errore nel caricamento dei dati", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDashboardData();
    }, [currentSedeId]);

    // Cambiando mese si aggiorna solo il riquadro dei mandati usciti dai KPI.
    useEffect(() => {
        let attuale = true;
        const { from, to } = limitiMese(mese);
        AmministrazioneService.getImportiNonRiscossi({ sedeId: currentSedeId ?? 'ALL', dal: from, al: to })
            .then(res => { if (attuale && res.success) setNonRiscossi(res.data || []); })
            .catch(() => {});
        return () => { attuale = false; };
    }, [mese, currentSedeId]);

    // Aggiunta spesa fissa
    const handleAddSpesaFissa = async () => {
        if (!nuovaSpesa.voce || nuovaSpesa.importo <= 0) {
            addToast("Inserisci un nome e un importo valido", "error");
            return;
        }

        try {
            const payload = {
                sedeId: currentSedeId === 'ALL' ? null : currentSedeId,
                voce: nuovaSpesa.voce,
                importo: nuovaSpesa.importo
            };

            await AmministrazioneService.addSpesaFissa(payload);
            addToast("Spesa fissa aggiunta", "success");
            setShowAddSpesaModal(false);
            setNuovaSpesa({ voce: '', importo: 0 });
            loadDashboardData();
        } catch (e) {
            addToast("Errore durante l'aggiunta della spesa", "error");
        }
    };

    // Eliminazione spesa fissa
    const handleDeleteSpesa = async (id: number) => {
        if (!window.confirm("Sei sicuro di voler eliminare questa spesa fissa?")) return;
        try {
            await AmministrazioneService.deleteSpesaFissa(id);
            addToast("Spesa fissa eliminata", "success");
            loadDashboardData();
        } catch (e) {
            addToast("Errore durante l'eliminazione", "error");
        }
    };

    const formatCurrency = (amount: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
    const formatDate = (dateString: string) => dateString ? new Date(dateString).toLocaleDateString('it-IT') : '-';

    if (loading) return <div className="flex h-screen items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-blue-600 w-10 h-10" /></div>;

    const utileReale = stats.fatturato - stats.spese - totaleSpeseFisse;

    return (
        <div className="p-6 space-y-6 pb-24">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Contabilità</h1>
                    <p className="text-sm text-gray-500">Report finanziario: <span className="font-semibold text-gray-700">{currentSedeName}</span></p>
                </div>
                <div className="flex gap-2">
                    {/*
                      La view ricarica solo al cambio sede: il pulsante serve a vedere le modifiche
                      fatte in altre pagine senza uscire.
                    */}
                    <button
                        onClick={loadDashboardData}
                        disabled={loading}
                        title="Ricarica fatturato, incassi, spese e non saldati"
                        className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-50 shadow-sm flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <Loader2 size={16} className={loading ? 'animate-spin' : ''}/>
                        {loading ? 'Aggiornamento…' : 'Aggiorna'}
                    </button>

                    {/* Registro generale (entrate e uscite di mandati, servizi, collaboratori). */}
                    <button
                        onClick={() => navigate('/amministrazione/registro-movimenti')}
                        className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-100 shadow-sm flex items-center gap-2 transition-colors"
                        title="Tutte le entrate e le uscite dell'azienda, filtrabili per periodo"
                    >
                        <Receipt size={16}/> Vedi tutti i movimenti
                    </button>

                </div>
            </div>

            {/* Spese fisse mensili agenzia */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                    <div className="flex items-center gap-2">
                        <Building2 className="text-gray-400" size={20}/>
                        <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Spese Mensili Fisse Agenzia</h3>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <span className="text-xs text-gray-500 font-medium uppercase">Totale Mensile: </span>
                            <span className="font-bold text-red-600">{formatCurrency(totaleSpeseFisse)}</span>
                        </div>
                        {/* Storico variazioni spese fisse */}
                        <button
                            onClick={() => setShowStoricoSpeseModal(true)}
                            className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition-colors"
                            title="Cronologia inserimenti ed eliminazioni di spese fisse"
                        >
                            <Clock size={14}/> Vedi storico
                        </button>
                        <button
                            onClick={() => {
                                if (currentSedeId === 'ALL' || currentSedeId == null) {
                                    setShowSelectSedeModal(true);
                                } else {
                                    setShowAddSpesaModal(true);
                                }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-md flex items-center justify-center transition-colors"
                            title="Aggiungi Spesa Fissa"
                        >
                            <PlusCircle size={18}/>
                        </button>
                    </div>
                </div>
                <div className="p-4">
                    {speseFisse.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">Nessuna spesa fissa inserita. Clicca sul + per aggiungerne una.</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {speseFisse.map(spesa => (
                                <div key={spesa.id} className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col relative group">
                                    {/* Elimina (visibile in hover) */}
                                    <button
                                        onClick={() => handleDeleteSpesa(spesa.id)}
                                        className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Elimina"
                                    >
                                        <X size={16}/>
                                    </button>

                                    <span className="text-gray-400 mb-1"><CreditCard size={16}/></span>
                                    <span className="text-xs text-gray-500 font-medium truncate pr-4 mb-1" title={spesa.voce}>{spesa.voce}</span>
                                    <span className="font-bold text-gray-900">{formatCurrency(spesa.importo)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modale aggiunta spesa fissa */}
            {showAddSpesaModal && (
                <Modal title="Aggiungi Spesa Fissa Mensile" onClose={() => setShowAddSpesaModal(false)}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Descrizione Spesa</label>
                            <input
                                type="text"
                                placeholder="es. Affitto Ufficio"
                                className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                value={nuovaSpesa.voce}
                                onChange={e => setNuovaSpesa({...nuovaSpesa, voce: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Importo Mensile (€)</label>
                            <NumberInput
                                min={0}
                                step="0.01"
                                placeholder="0.00"
                                className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                value={nuovaSpesa.importo || ''}
                                onChange={e => setNuovaSpesa({...nuovaSpesa, importo: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                            />
                        </div>
                        <button
                            onClick={handleAddSpesaFissa}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700"
                        >
                            Salva Spesa Fissa
                        </button>
                    </div>
                </Modal>
            )}

            {/* Con "Tutte le sedi" niente aggiunta: ogni spesa fissa va legata a una sede. */}
            {showSelectSedeModal && (
                <Modal title="Seleziona prima una sede" onClose={() => setShowSelectSedeModal(false)}>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-700">
                            Le spese fisse sono legate a una <strong>sede specifica</strong> (affitto, utenze, ecc.).
                        </p>
                        <p className="text-sm text-slate-700">
                            Seleziona prima una sede dalla sidebar (in alto a sinistra), poi torna qui per aggiungere la spesa fissa.
                        </p>
                        <button
                            onClick={() => setShowSelectSedeModal(false)}
                            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-bold hover:bg-blue-700"
                        >
                            Ho capito
                        </button>
                    </div>
                </Modal>
            )}

            {/* Entrate e uscite una tantum: contano solo nel loro mese. */}
            <MovimentiUnaTantumCard sedeId={currentSedeId} mese={mese} onMeseChange={setMese} />

            {/* Mandati: movimenti, saldi e fatturazione */}
            <div className="mt-10 mb-2 pb-2 border-b-2 border-slate-200 flex items-center gap-2">
                <h2 className="text-xs font-black text-slate-600 uppercase tracking-widest">Servizi Investigativi</h2>
                <span className="text-[10px] font-medium text-slate-400">Incassi, saldi e fatturazione delle pratiche investigative</span>
                {/* Registro movimenti filtrato sui mandati. */}
                <button
                    onClick={() => navigate('/amministrazione/registro-movimenti?pre=PRATICHE')}
                    className="ml-auto bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 px-3 py-1 rounded-md text-[11px] font-bold flex items-center gap-1.5 transition-colors"
                    title="Apri il Registro Movimenti pre-filtrato sui mandati"
                >
                    <Receipt size={12}/> Vedi tutti
                </button>
            </div>

            {/* Movimenti operativi mandati */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="p-4 border-b bg-gray-50">
                    <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Ultimi Movimenti Operativi (Servizi Investigativi)</h3>
                </div>
                {movimentiPratiche.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nessun movimento registrato sui mandati.</p>
                ) : (
                    <div className="overflow-x-auto -mx-2 px-2">
                        <table className="w-full min-w-[700px] text-left">
                            <thead className="bg-white border-b text-xs uppercase text-gray-500 font-bold">
                            <tr>
                                <th className="p-4">Data</th>
                                <th className="p-4">Descrizione</th>
                                <th className="p-4">Mandato</th>
                                <th className="p-4 text-right">Importo</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {movimentiPratiche.map((mov) => (
                                <tr key={mov.id} className="hover:bg-gray-50">
                                    <td className="p-4">{formatDate(mov.data)}</td>
                                    <td className="p-4">{mov.descrizione}</td>
                                    <td className="p-4 text-gray-700">{mov.praticaRef || '-'}</td>
                                    <td className={`p-4 text-right font-bold ${mov.importo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {mov.importo > 0 ? '+' : ''} {formatCurrency(mov.importo)}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pratiche non saldate */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Servizi Investigativi Non Ancora Saldati</h3>
                    {praticheNonSaldate.length > 0 && (
                        <span className="text-xs font-bold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-1 rounded-full">
                            {praticheNonSaldate.length} pratiche · Da riscuotere: {formatCurrency(praticheNonSaldate.reduce((acc, p) => acc + p.importoResiduo, 0))}
                        </span>
                    )}
                </div>
                {praticheNonSaldate.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nessuna pratica con saldo residuo.</p>
                ) : (
                    <div className="overflow-x-auto -mx-2 px-2">
                        <table className="w-full min-w-[850px] text-left">
                            <thead className="bg-white border-b text-xs uppercase text-gray-500 font-bold">
                            <tr>
                                <th className="p-4">Rif. / Nome</th>
                                <th className="p-4">Cliente</th>
                                <th className="p-4">Stato</th>
                                <th className="p-4 text-right">Totale Concordato</th>
                                <th className="p-4 text-right">Già Incassato</th>
                                <th className="p-4 text-right">Da Riscuotere</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {praticheNonSaldate.map((p) => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="p-4">
                                        <p className="font-semibold text-gray-800">{p.rif}</p>
                                        <p className="text-xs text-gray-400">{p.nome}</p>
                                    </td>
                                    <td className="p-4 text-gray-700">{p.clienteNome}</td>
                                    <td className="p-4">
                                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">{p.stato}</span>
                                    </td>
                                    <td className="p-4 text-right text-gray-700">{formatCurrency(p.importoTotale)}</td>
                                    <td className="p-4 text-right text-green-600 font-medium">{formatCurrency(p.importoIncassato)}</td>
                                    <td className="p-4 text-right font-bold text-orange-600">{formatCurrency(p.importoResiduo)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {/* Pratiche non fatturate */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Receipt size={16} className="text-gray-400"/>
                        {/* Etichetta generica: le pratiche con fatturazione incompleta possono essere ancora in corso. */}
                        <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Servizi Investigativi con Fatturazione in Sospeso</h3>
                    </div>
                    {praticheNonFatturate.length > 0 && (
                        <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-full">
                            {praticheNonFatturate.length} pratiche · Da fatturare: {formatCurrency(praticheNonFatturate.reduce((acc, p) => acc + p.importoDaFatturare, 0))}
                        </span>
                    )}
                </div>
                {praticheNonFatturate.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nessuna pratica con fatturazione in sospeso.</p>
                ) : (
                    <div className="overflow-x-auto -mx-2 px-2">
                        <table className="w-full min-w-[800px] text-left">
                            <thead className="bg-white border-b text-xs uppercase text-gray-500 font-bold">
                            <tr>
                                <th className="p-4">Rif. / Nome</th>
                                <th className="p-4">Cliente</th>
                                <th className="p-4 text-right">Totale Concordato</th>
                                <th className="p-4 text-right">Già Fatturato</th>
                                <th className="p-4 text-right">Da Fatturare</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {praticheNonFatturate.map((p) => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="p-4">
                                        <p className="font-semibold text-gray-800">{p.rif}</p>
                                        <p className="text-xs text-gray-400">{p.nome}</p>
                                    </td>
                                    <td className="p-4 text-gray-700">{p.clienteNome}</td>
                                    <td className="p-4 text-right text-gray-700">{formatCurrency(p.importoTotale)}</td>
                                    <td className="p-4 text-right text-emerald-600 font-medium">{formatCurrency(p.importoFatturato)}</td>
                                    <td className="p-4 text-right font-bold text-red-600">{formatCurrency(p.importoDaFatturare)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Servizi fiduciari: movimenti, saldi e fatturazione */}
            <div className="mt-10 mb-2 pb-2 border-b-2 border-indigo-200 flex items-center gap-2">
                <Shield size={14} className="text-indigo-500"/>
                <h2 className="text-xs font-black text-indigo-700 uppercase tracking-widest">Servizi Fiduciari</h2>
                <span className="text-[10px] font-medium text-slate-400">Incassi, saldi e fatturazione dei servizi fiduciari</span>
                {/* Registro movimenti filtrato sui servizi. */}
                <button
                    onClick={() => navigate('/amministrazione/registro-movimenti?pre=SERVIZI')}
                    className="ml-auto bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 px-3 py-1 rounded-md text-[11px] font-bold flex items-center gap-1.5 transition-colors"
                    title="Apri il Registro Movimenti pre-filtrato sui servizi fiduciari"
                >
                    <Receipt size={12}/> Vedi tutti
                </button>
            </div>

            {/* Movimenti operativi servizi fiduciari */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="p-4 border-b bg-gray-50">
                    <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Ultimi Movimenti Operativi (Servizi Fiduciari)</h3>
                </div>
                {movimentiServizi.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nessun movimento registrato sui servizi fiduciari.</p>
                ) : (
                    <div className="overflow-x-auto -mx-2 px-2">
                        <table className="w-full min-w-[700px] text-left">
                            <thead className="bg-white border-b text-xs uppercase text-gray-500 font-bold">
                            <tr>
                                <th className="p-4">Data</th>
                                <th className="p-4">Descrizione</th>
                                <th className="p-4">Servizio</th>
                                <th className="p-4 text-right">Importo</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {movimentiServizi.map((mov) => (
                                <tr key={mov.id} className="hover:bg-gray-50">
                                    <td className="p-4">{formatDate(mov.data)}</td>
                                    <td className="p-4">{mov.descrizione}</td>
                                    <td className="p-4 text-gray-700">{mov.praticaRef || '-'}</td>
                                    <td className={`p-4 text-right font-bold ${mov.importo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {mov.importo > 0 ? '+' : ''} {formatCurrency(mov.importo)}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Servizi non saldati */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Servizi Fiduciari Non Ancora Saldati</h3>
                    {serviziNonSaldati.length > 0 && (
                        <span className="text-xs font-bold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-1 rounded-full">
                            {serviziNonSaldati.length} servizi · Da riscuotere: {formatCurrency(serviziNonSaldati.reduce((acc, s) => acc + s.importoResiduo, 0))}
                        </span>
                    )}
                </div>
                {serviziNonSaldati.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nessun servizio con saldo residuo.</p>
                ) : (
                    <div className="overflow-x-auto -mx-2 px-2">
                        <table className="w-full min-w-[850px] text-left">
                            <thead className="bg-white border-b text-xs uppercase text-gray-500 font-bold">
                            <tr>
                                <th className="p-4">Servizio</th>
                                <th className="p-4">Cliente</th>
                                <th className="p-4">Stato</th>
                                <th className="p-4 text-right">Totale Proforma</th>
                                <th className="p-4 text-right">Già Incassato</th>
                                <th className="p-4 text-right">Da Riscuotere</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {serviziNonSaldati.map((s) => (
                                <tr key={s.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/servizi/${s.id}`)}>
                                    <td className="p-4 font-semibold text-gray-800">{s.nome}</td>
                                    <td className="p-4 text-gray-700">{s.clienteNome}</td>
                                    <td className="p-4">
                                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">{s.stato}</span>
                                    </td>
                                    <td className="p-4 text-right text-gray-700">{formatCurrency(s.importoTotale)}</td>
                                    <td className="p-4 text-right text-green-600 font-medium">{formatCurrency(s.importoIncassato)}</td>
                                    <td className="p-4 text-right font-bold text-orange-600">{formatCurrency(s.importoResiduo)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Servizi con fatturazione in sospeso */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Receipt size={16} className="text-gray-400"/>
                        <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Servizi Fiduciari con Fatturazione in Sospeso</h3>
                    </div>
                    {serviziNonFatturati.length > 0 && (
                        <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-full">
                            {serviziNonFatturati.length} servizi · Da fatturare: {formatCurrency(serviziNonFatturati.reduce((acc, s) => acc + s.importoDaFatturare, 0))}
                        </span>
                    )}
                </div>
                {serviziNonFatturati.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nessun servizio con fatturazione in sospeso.</p>
                ) : (
                    <div className="overflow-x-auto -mx-2 px-2">
                        <table className="w-full min-w-[800px] text-left">
                            <thead className="bg-white border-b text-xs uppercase text-gray-500 font-bold">
                            <tr>
                                <th className="p-4">Servizio</th>
                                <th className="p-4">Cliente</th>
                                <th className="p-4 text-right">Totale Proforma</th>
                                <th className="p-4 text-right">Già Fatturato</th>
                                <th className="p-4 text-right">Da Fatturare</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {serviziNonFatturati.map((s) => (
                                <tr key={s.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/servizi/${s.id}`)}>
                                    <td className="p-4 font-semibold text-gray-800">{s.nome}</td>
                                    <td className="p-4 text-gray-700">{s.clienteNome}</td>
                                    <td className="p-4 text-right text-gray-700">{formatCurrency(s.importoTotale)}</td>
                                    <td className="p-4 text-right text-emerald-600 font-medium">{formatCurrency(s.importoFatturato)}</td>
                                    <td className="p-4 text-right font-bold text-red-600">{formatCurrency(s.importoDaFatturare)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/*
              Banner mandati con importi non riscossi. Archiviare o eliminare un mandato
              lo toglie dai KPI e dal "Da Riscuotere", ma gli importi restano dovuti:
              li teniamo separati senza nasconderli. Sta in fondo alla pagina per essere
              meno invasivo, ma resta visibile.
            */}
            {!loading && nonRiscossi.length > 0 && (
                <button
                    onClick={() => setShowNonRiscossiModal(true)}
                    className="w-full text-left bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-center gap-4 hover:bg-amber-100 transition-colors shadow-sm mt-6"
                    title="Apri l'elenco dei mandati con importi ancora da riscuotere usciti dai KPI"
                >
                    <div className="shrink-0 bg-amber-100 border border-amber-300 rounded-lg p-2">
                        <AlertTriangle className="text-amber-700" size={22}/>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-amber-900 text-sm">
                            {nonRiscossi.length} {nonRiscossi.length === 1 ? 'voce' : 'voci'} (mandati e servizi) con importi non riscossi o non fatturati
                            <span className="font-medium"> · usciti dai KPI a {new Date(`${mese}-01T00:00:00`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</span>
                            {nEliminati > 0 && (
                                <span className="ml-2 text-xs font-bold text-red-700 bg-red-100 border border-red-200 px-2 py-0.5 rounded-full align-middle">
                                    {nEliminati} eliminat{nEliminati === 1 ? 'o' : 'i'}
                                </span>
                            )}
                        </p>
                        {/* Due totali affiancati, mai sommati: sono debiti diversi sullo stesso mandato. */}
                        <p className="text-sm font-bold text-amber-800 mt-0.5">
                            {formatCurrency(totaleNonRiscossi)} <span className="font-medium text-xs text-amber-800/80">non riscossi</span>
                            <span className="mx-2 text-amber-400">·</span>
                            {formatCurrency(totaleNonFatturato)} <span className="font-medium text-xs text-amber-800/80">non fatturati</span>
                        </p>
                        <p className="text-xs text-amber-800/80 mt-0.5">
                            Mandati (archiviati, sospesi, annullati o eliminati) e servizi fiduciari eliminati su cui resta un incasso
                            da fare o una fattura da emettere. Esclusi dai KPI, dal “Da Riscuotere” e dal “Da Fatturare”, non dalla contabilità. Clicca per l'elenco.
                        </p>
                    </div>
                    <span className="shrink-0 text-xs font-bold text-amber-800 bg-white border border-amber-300 px-3 py-1.5 rounded-lg">
                        Vedi elenco
                    </span>
                </button>
            )}

            {showNonRiscossiModal && (
                <ImportiNonRiscossiModal
                    onClose={() => setShowNonRiscossiModal(false)}
                    sedeId={currentSedeId ?? 'ALL'}
                    dalIniziale={limitiMese(mese).from}
                    alIniziale={limitiMese(mese).to}
                />
            )}

            {/* Modale storico spese fisse */}
            {showStoricoSpeseModal && (
                <StoricoSpeseFisseModal
                    onClose={() => setShowStoricoSpeseModal(false)}
                    sedeId={currentSedeId}
                />
            )}
        </div>
    );
};

// Modale storico variazioni spese fisse (inserimenti ed eliminazioni).

interface StoricoSpeseFisseModalProps {
    onClose: () => void;
    sedeId: number | 'ALL' | null;
}

const StoricoSpeseFisseModal: React.FC<StoricoSpeseFisseModalProps> = ({ onClose, sedeId }) => {
    const today = localIsoDate();
    const sixMonthsAgo = (() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 6);
        return localIsoDate(d);
    })();

    const [from, setFrom] = useState<string>(sixMonthsAgo);
    const [to, setTo] = useState<string>(today);
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await AmministrazioneService.getStoricoSpeseFisse({
                    from, to,
                    sedeId: (sedeId ?? 'ALL') as number | 'ALL',
                });
                if (!cancelled && res.success) setRows(res.data || []);
            } catch (e) {
                console.error('Errore caricamento storico spese fisse', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [from, to, sedeId]);

    const fmtEuro = (n?: number) =>
        n == null ? '—'
            : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

    const fmtAt = (iso?: string) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-3xl p-5 shadow-xl max-h-[85vh] flex flex-col">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-amber-600" />
                            Storico variazioni Spese Fisse
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">Inserimenti ed eliminazioni di spese fisse mensili, ordinati per data decrescente.</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-800 p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-wrap gap-3 items-center mb-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                        Da
                        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-sm" />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                        A
                        <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-sm" />
                    </label>
                </div>

                <div className="overflow-y-auto -mx-2 px-2 flex-1">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 sticky top-0">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Data</th>
                                <th className="text-left px-3 py-2 font-medium">Azione</th>
                                <th className="text-left px-3 py-2 font-medium">Voce</th>
                                <th className="text-right px-3 py-2 font-medium">Importo</th>
                                <th className="text-left px-3 py-2 font-medium">Utente</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />Caricamento…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-500">Nessuna variazione nel periodo selezionato.</td></tr>
                            ) : rows.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtAt(r.at)}</td>
                                    <td className="px-3 py-2">
                                        {r.action === 'INSERT' ? (
                                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Inserita</span>
                                        ) : r.action === 'DELETE' ? (
                                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-800">Eliminata</span>
                                        ) : (
                                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{r.action}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">{r.voce || '—'}</td>
                                    <td className="px-3 py-2 text-right font-medium text-slate-800">{fmtEuro(r.importo)}</td>
                                    <td className="px-3 py-2 text-slate-600">{r.byUserName || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// Modale "Mandati con importi non riscossi".
//
// Le date filtrano sulla data di uscita dai KPI (archiviazione o eliminazione),
// non sulla creazione: interessa sapere cosa si è chiuso senza incassare e quando.
//
// Le righe eliminate mostrano solo l'id: la cancellazione anonimizza committente
// e soggetto e il mandato non è più navigabile.
//
// Di default nessun filtro, così il totale coincide con quello del banner.

interface ImportiNonRiscossiModalProps {
    onClose: () => void;
    sedeId: number | 'ALL' | null;
    // Periodo con cui si apre l'elenco (il mese della pagina); si può cambiare o azzerare.
    dalIniziale?: string;
    alIniziale?: string;
}

const ImportiNonRiscossiModal: React.FC<ImportiNonRiscossiModalProps> = ({ onClose, sedeId, dalIniziale, alIniziale }) => {
    const navigate = useNavigate();

    const [dal, setDal] = useState<string>(dalIniziale || '');
    const [al, setAl] = useState<string>(alIniziale || '');
    const [rows, setRows] = useState<PraticaNonSaldata[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await AmministrazioneService.getImportiNonRiscossi({
                    sedeId: (sedeId ?? 'ALL') as number | 'ALL',
                    dal: dal || undefined,
                    al: al || undefined,
                });
                if (!cancelled && res.success) setRows(res.data || []);
            } catch (e) {
                console.error('Errore caricamento mandati con importi non riscossi', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [dal, al, sedeId]);

    const fmtEuro = (n?: number) =>
        n == null ? '—'
            : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

    const fmtGiorno = (iso?: string | null) => {
        if (!iso) return null;
        return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const totaleResiduo = rows.reduce((acc, r) => acc + r.importoResiduo, 0);
    const totaleDaFatturare = rows.reduce((acc, r) => acc + (r.importoDaFatturare || 0), 0);
    const filtroAttivo = Boolean(dal || al);

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl p-5 shadow-xl max-h-[85vh] flex flex-col">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600"/>
                            Mandati e servizi con importi non riscossi o non fatturati
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Mandati (archiviati, sospesi, annullati o eliminati) e servizi fiduciari eliminati su cui resta un incasso da fare
                            (<em>non riscosso</em>) o una fattura da emettere (<em>non fatturato</em>). Sono due debiti distinti:
                            una voce pagata in contanti e mai fatturata ha zero da riscuotere e l'intero importo da fatturare.
                            Fuori dai KPI, ma dovuti a tutti gli effetti. Dei mandati eliminati resta il solo numero identificativo.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-800 p-1">
                        <X className="w-5 h-5"/>
                    </button>
                </div>

                <div className="flex flex-wrap gap-3 items-center mb-3">
                    <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">Usciti dai KPI tra</span>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                        Dal
                        <input type="date" value={dal} max={al || undefined} onChange={(e) => setDal(e.target.value)}
                               className="px-2 py-1 border border-slate-200 rounded text-sm"/>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                        Al
                        <input type="date" value={al} min={dal || undefined} onChange={(e) => setAl(e.target.value)}
                               className="px-2 py-1 border border-slate-200 rounded text-sm"/>
                    </label>
                    {filtroAttivo && (
                        <button
                            onClick={() => { setDal(''); setAl(''); }}
                            className="text-xs font-bold text-slate-600 hover:text-slate-900 underline"
                        >
                            Azzera filtro
                        </button>
                    )}
                    {!filtroAttivo && (
                        <span className="text-xs text-slate-400">Nessun filtro: tutto lo storico.</span>
                    )}
                </div>

                <div className="overflow-y-auto -mx-2 px-2 flex-1">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 sticky top-0">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Uscito il</th>
                                <th className="text-left px-3 py-2 font-medium">Motivo</th>
                                <th className="text-left px-3 py-2 font-medium">Rif. / Nome</th>
                                <th className="text-left px-3 py-2 font-medium">Cliente</th>
                                <th className="text-right px-3 py-2 font-medium">Totale Concordato</th>
                                <th className="text-right px-3 py-2 font-medium">Già Incassato</th>
                                <th className="text-right px-3 py-2 font-medium">Non Riscosso</th>
                                <th className="text-right px-3 py-2 font-medium">Non Fatturato</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                                    <Loader2 className="w-5 h-5 animate-spin inline-block mr-2"/>Caricamento…
                                </td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                                    {filtroAttivo
                                        ? 'Nessun mandato o servizio con importi non riscossi o non fatturati nel periodo selezionato.'
                                        : 'Nessun mandato o servizio con importi non riscossi o non fatturati.'}
                                </td></tr>
                            ) : rows.map((r) => {
                                const giorno = fmtGiorno(r.dataUscita);
                                const eliminato = r.motivoUscita === 'ELIMINATO';
                                const isServizio = r.entityType === 'SERVIZIO';
                                return (
                                    <tr
                                        key={`${r.entityType || 'PRATICA'}-${r.motivoUscita}-${r.id}`}
                                        // Eliminato = non più navigabile: la riga non è cliccabile.
                                        onClick={eliminato ? undefined : () => navigate(isServizio ? `/servizi/${r.id}` : `/pratiche/${r.id}`)}
                                        className={`border-t border-slate-100 ${eliminato ? 'bg-red-50/40' : 'hover:bg-amber-50/60 cursor-pointer'}`}
                                    >
                                        {/*
                                          Gli archiviati più vecchi non hanno data: li mostriamo in ogni periodo
                                          invece di farli sparire.
                                        */}
                                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                            {giorno ?? (
                                                <span className="text-slate-400 italic" title="Mandato archiviato prima che la data di archiviazione venisse tracciata. Compare in ogni periodo.">
                                                    data non disponibile
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {eliminato ? (
                                                <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800"
                                                      title={isServizio
                                                          ? "Servizio eliminato: fuori dai KPI, ma l'importo resta dovuto. Le cifre sono congelate al momento dell'eliminazione."
                                                          : "Mandato eliminato. Del mandato resta solo il numero identificativo: nome, riferimento e cliente sono stati anonimizzati dall'eliminazione."}>
                                                    Eliminato
                                                </span>
                                            ) : r.motivoUscita === 'SOSPESO' ? (
                                                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800"
                                                      title="Mandato sospeso: fuori dai KPI, ma il residuo resta dovuto. Riattivandolo torna nel Da Riscuotere.">
                                                    Sospeso
                                                </span>
                                            ) : r.motivoUscita === 'ANNULLATO' ? (
                                                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700">
                                                    Annullato
                                                </span>
                                            ) : (
                                                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                                    Archiviato
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {eliminato ? (
                                                isServizio ? (
                                                    <>
                                                        <p className="font-semibold text-slate-800">{r.nome || `Servizio #${r.id}`}</p>
                                                        <p className="text-xs text-slate-400">Servizio fiduciario</p>
                                                    </>
                                                ) : (
                                                    <p className="font-semibold text-slate-800">Mandato #{r.id}</p>
                                                )
                                            ) : (
                                                <>
                                                    <p className="font-semibold text-slate-800">{r.rif || '—'}</p>
                                                    <p className="text-xs text-slate-400">{r.nome}</p>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-slate-700">
                                            {eliminato
                                                ? (isServizio ? (r.clienteNome || '—') : <span className="text-slate-400 italic">dati rimossi</span>)
                                                : r.clienteNome}
                                        </td>
                                        <td className="px-3 py-2 text-right text-slate-700">{fmtEuro(r.importoTotale)}</td>
                                        <td className="px-3 py-2 text-right text-green-600 font-medium">{fmtEuro(r.importoIncassato)}</td>
                                        {/* Zero in grigio: attira l'occhio solo su ciò che manca davvero. */}
                                        <td className={`px-3 py-2 text-right font-bold ${
                                            !r.importoResiduo ? 'text-slate-300' : eliminato ? 'text-red-700' : 'text-amber-700'}`}>
                                            {fmtEuro(r.importoResiduo)}
                                        </td>
                                        <td className={`px-3 py-2 text-right font-bold ${
                                            !r.importoDaFatturare ? 'text-slate-300' : eliminato ? 'text-red-700' : 'text-amber-700'}`}>
                                            {fmtEuro(r.importoDaFatturare ?? 0)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {!loading && rows.length > 0 && (
                    <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between items-center">
                        <p className="text-xs text-slate-500">
                            {rows.length} {rows.length === 1 ? 'mandato' : 'mandati'}
                            {filtroAttivo ? ' nel periodo selezionato' : ' in totale'}
                        </p>
                        {/* Due totali separati: sommarli conterebbe due volte lo stesso importo. */}
                        <p className="text-sm font-bold text-slate-800">
                            Non riscosso: <span className="text-amber-700">{fmtEuro(totaleResiduo)}</span>
                            <span className="mx-2 text-slate-300">·</span>
                            Non fatturato: <span className="text-amber-700">{fmtEuro(totaleDaFatturare)}</span>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AmministrazioneView;